import { Box, Dialog, InputAdornment, Typography } from "@mui/material";
import styles from "../ExpenseRequest.module.scss";
import { AttachMoney, Close } from "@mui/icons-material";
import { useFormik } from "formik";
import {
  CommonButton,
  CommonOutlinedButton,
  CustomRequiredInputLabel,
  CustomTextFeild,
} from "../../../common/StyledComponents/StyledFields";
import { textFieldStyles } from "./CreateExpenseRequest";
import fileUpload from "../../../../assets/FileUpload.svg";
import deleteIcon from "../../../../assets/svg/delete_outline.svg";
import { useCallback } from "react";
import { number, object, string } from "yup";
import { InsertDriveFileOutlined } from "@mui/icons-material";

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in byte

const UploadReceipt = (props: any) => {
  const { handleClose, open, handleUploadReceipt } = props;

  const initialValues = {
    amount: null,
    description: "",
    fileName: "",
    file: null,
  };

  const validationSchema = object().shape({
    amount: number()
      .required("Amount is required")
      .min(1, "Minimum amount must be $1"),
    description: string().trim().required("Description is required"),
    fileName: string()
      .trim()
      .required("Please upload a receipt")
      .test(
        "fileType",
        "Please upload only JPG or PDF files",
        function (value) {
          const { file } = this.parent;
          if (!file) return true; // If no file is selected, validation passes
          const allowedExtensions = /\.(jpg|jpeg|pdf)$/i;
          return allowedExtensions.test(file.name);
        }
      )
      .test(
        "fileSize",
        "The file size should not exceed 10MB",
        function (value) {
          const { file } = this.parent;
          if (!file) return true; // If no file is selected, validation passes
          return file.size <= MAX_FILE_SIZE;
        }
      ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit(values) {
      handleUploadReceipt(values);
      formik.resetForm();
    },
    enableReinitialize: true,
  });

  const handleDragOver = useCallback((event: any) => {
    event.preventDefault();
    // setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    // setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((event: any) => {
    event.preventDefault();
    const files: any = Array.from(event.dataTransfer?.files);

    if (files?.length > 0) {
      formik.setFieldValue("file", files[0]);
      formik.setFieldValue("fileName", files?.[0]?.name);
    }
  }, []);

  const handleFileChange = (event: any) => {
    const files: any = Array.from(event.target.files);
    if (files?.length > 0) {
      formik.setFieldValue("file", files[0]);
      formik.setFieldValue("fileName", files?.[0]?.name);
    }
  };

  const handleSelectFile = () => {
    document?.getElementById("file-input")?.click();
  };

  const handleRemoveFile = () => {
    formik.setFieldValue("file", null);
    formik.setFieldValue("fileName", "");
  };

  const renderFieldsComponent = () => {
    return (
      <Box>
        <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
          {CustomRequiredInputLabel("Amount", true)}
          <CustomTextFeild
            fullWidth
            required
            name="amount"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoney fontSize="small" sx={{ position: "absolute" }} />
                </InputAdornment>
              ),
            }}
            value={formik.values.amount}
            placeholder="Enter amount"
            onChange={(e) => {
              const numericValue = e.target.value
                .replace(/[^\d.]/g, "")
                .slice(0, 15); // Allow only the first 10 numbers
              formik.setFieldValue("amount", numericValue);
            }}
            sx={
              formik.touched.amount && formik.errors.amount
                ? textFieldStyles.errorTextField
                : textFieldStyles.textField
            }
          />
          {formik.touched.amount && formik?.errors?.amount && (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {formik?.errors?.amount}
            </Typography>
          )}
        </Box>
        <Box sx={{ mt: { xs: "5px", sm: "10px" } }}>
          {CustomRequiredInputLabel("Description", true)}
          <textarea
            name="description"
            placeholder="Text Description"
            className={styles.text_area_field}
            value={formik.values.description}
            onChange={formik.handleChange}
            style={{ height: "85px" }}
          />
          {formik.touched.description && formik?.errors?.description && (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {formik?.errors?.description}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            mt: "10px",
            p: "15px",
            border: "1px dashed black",
            borderRadius: "6px",
            textAlign: "center",
          }}
          onDragOver={handleDragOver}
          // onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="file-input"
            accept="image/jpeg,image/jpg,.pdf"
            style={{ display: "none" }}
            onChange={handleFileChange}
            name="file"
          />
          <Box
            component={"img"}
            src={fileUpload}
            alt="Upload file"
            sx={{ width: "35px" }}
          />
          <Typography className={styles.drag_drop_txt}>
            Drag and Drop here (Only .Jpg or .Pdf, max 10MB)
          </Typography>
          <Typography className={styles.or_text}>or</Typography>
          <CommonButton onClick={handleSelectFile}>Select File</CommonButton>
        </Box>
        {formik.values.fileName && (
          <Box
            sx={{
              mt: "10px",
              background: "#7EAAFF38",
              p: "10px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <InsertDriveFileOutlined sx={{ mr: "5px", fontSize: "32px" }} />
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  mb: "4px",
                }}
              >
                <Typography className={styles.file_name}>
                  {formik.values.fileName}
                </Typography>
                <img
                  src={deleteIcon}
                  alt="delete item"
                  style={{ cursor: "pointer" }}
                  onClick={handleRemoveFile}
                />
              </Box>
              <Box
                sx={{
                  width: "100%",
                  background: "#22A699",
                  height: "5px",
                  borderRadius: "2.5px",
                }}
              />
            </Box>
          </Box>
        )}
        {formik.touched.fileName && formik?.errors?.fileName && (
          <Typography sx={{ color: "red", fontSize: "12px" }}>
            {formik?.errors?.fileName}
          </Typography>
        )}
      </Box>
    );
  };

  const renderButtonsComponent = () => {
    return (
      <Box sx={{ mt: "20px" }}>
        <CommonButton sx={{ mr: "10px" }} onClick={() => formik.handleSubmit()}>
          Upload
        </CommonButton>
        <CommonOutlinedButton onClick={handleClose}>
          Cancel
        </CommonOutlinedButton>
      </Box>
    );
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "600px" },
          maxWidth: { xs: "100%", sm: "600px" },
          margin: { xs: "10px", sm: "20px" },
        },
      }}
    >
      <Box
        className={styles.add_vendor_main_container}
        sx={{
          width: "100%",
        }}
      >
        <Box className={styles.dialog_title_container}>
          <Typography className={styles.title}>Upload Receipt</Typography>
          <Close
            sx={{ color: "white", cursor: "pointer" }}
            onClick={handleClose}
          />
        </Box>
        <Box className={styles.dialog_inner_container}>
          {renderFieldsComponent()}
          {renderButtonsComponent()}
        </Box>
      </Box>
    </Dialog>
  );
};

export default UploadReceipt;
